import { store as folioStore } from "../FolioStore.js";
import PortfoliosAPI from "../../api/portfolios/PortfoliosAPI.js";
import NotificationsAPI from "../../api/notification/NotificationsAPI.js";
import ProjectsAPI from '../../api/projects/ProjectsAPI.js';
import Router from "../../router/Router.js"
import SettingsFontAPI from "../../api/settings/SettingsFontAPI.js";
import {event, pageview} from "vue-gtag";
import {specificFontsLoader} from "../../settingsEditor/_helpers/fontLoaders.js";
import GoogleFontsHelper from "../../common/GoogleFontsHelper.js";
import {UserActions} from "./UserActions.js";
import {ChecklistInteractionEnum, PageTypesEnum, SettingsPanelPathsEnum} from "../../common/enums.js";
import MixpanelService from "../../analytics/mixpanel.js";
import ApiHelper from "@/scripts/api/ApiHelper.js";
import emitter from "@/scripts/common/utilities/emitter.js";

const store = folioStore.state;
const getters = folioStore.getters;
let templatePreviewsPromise = null;

function sortDefaultPortfolioFirst(portfolios) {
    if (!Array.isArray(portfolios)) {
        return portfolios;
    }

    const defaultPortfolioId = store.user?.defaultCollectionId;
    const defaultPortfolioIndex = portfolios.findIndex(portfolio => portfolio._id === defaultPortfolioId);

    if (defaultPortfolioIndex <= 0) {
        return portfolios;
    }

    const sortedPortfolios = [...portfolios];
    const [defaultPortfolio] = sortedPortfolios.splice(defaultPortfolioIndex, 1);
    sortedPortfolios.unshift(defaultPortfolio);
    return sortedPortfolios;
}

function shouldTrackEditorEvents() {
    return !folioStore.getters["presetEditor/isActive"];
}

function addSeenSystemNotifications(notificationIds) {
    if (!store.user) return;

    const currentIds = Array.isArray(store.user.seenSystemNotifications)
        ? store.user.seenSystemNotifications
        : [];

    store.user = {
        ...store.user,
        seenSystemNotifications: [...new Set([
            ...currentIds,
            ...notificationIds.filter(Boolean)
        ])]
    };
}

function getCurrentRoutePortfolioId(portfolioId) {
    const routerParams = Router.currentRoute.value.params;
    return portfolioId
        || routerParams.portfolioId
        || store.portfolio?.shortId
        || store.user.defaultCollectionId;
}

function shouldFetchPortfolioByShortId(portfolioId) {
    return !!Router.currentRoute.value.params.portfolioId
        && portfolioId === Router.currentRoute.value.params.portfolioId;
}

function redirectToSelectedPortfolioIfNeeded() {
    if(Router.currentRoute.value.fullPath !== "/home/onboarding" &&
        Router.currentRoute.value.fullPath !== "/home/portfolios" &&
        !Router.currentRoute.value.fullPath.includes("/home/portfolios/create-new-portfolio") &&
        Router.currentRoute.value.fullPath !== "/home/notifications" &&
        Router.currentRoute.value.fullPath !== "/home/prices" &&
        !Router.currentRoute.value.fullPath.includes("/home/settings") &&
        !Router.currentRoute.value.fullPath.includes("home/payment") &&
        !Router.currentRoute.value.fullPath.includes("home/domain") &&
        !Router.currentRoute.value.fullPath.includes("home/url") &&
        !Router.currentRoute.value.fullPath.includes("/reviews") &&
        !Router.currentRoute.value.params.portfolioId &&
        Router.currentRoute.value?.meta?.type !== 'view'
    ) {
        if(store.portfolioPages) {
            folioStore.commit("setPageToEdit", "home");
        }
        if(store.portfolio) {
            Router.push(`/home/portfolios/${store.portfolio.shortId}`)
        }
    }
}

function preloadSelectedPortfolioFonts() {
    if (!store.portfolio?.designSettings?.fontStyles) return;

    try {
        const fontStyles = Object.entries(store.portfolio.designSettings.fontStyles).map((i) => i[1]);
        GoogleFontsHelper.downloadSingleFonts(Object.entries(fontStyles).map((i) => i[1]));
        specificFontsLoader(fontStyles).catch(error => {
            console.log('Error while loading selected portfolio fonts', error);
        });
    } catch (error) {
        console.log('Error while preparing selected portfolio fonts', error);
    }
}

function selectCurrentRouteProject() {
    const { projectShortId } = Router.currentRoute.value.params;
    const fullPath = Router.currentRoute.value.fullPath;

    if (projectShortId) {
        const portfolioPage = store.portfolioPages?.find(page => page.shortId === projectShortId);
        const caseStudyPage = store.caseStudyPages?.find(page => page.shortId === projectShortId);

        if (portfolioPage) {
            folioStore.commit("setPageToEdit", projectShortId);
        } else if (caseStudyPage) {
            folioStore.commit("setCaseStudyToEdit", projectShortId);
        } else {
            folioStore.commit("setPageToEdit", "home");
            Router.push(`/home/portfolios/${store.portfolio.shortId}`);
        }
        return;
    }

    if (fullPath.includes("/about") && store.portfolioPages?.some(page => page.shortId === "about")) {
        folioStore.commit("setPageToEdit", "about");
        return;
    }

    if (fullPath.includes("/contact") && store.portfolioPages?.some(page => page.shortId === "contact")) {
        folioStore.commit("setPageToEdit", "contact");
        return;
    }

    folioStore.commit("setPageToEdit", "home");
}

export const PortfolioActions = {

    // get portfolio
    getPortfolio: async function (portfolioId) {
        await PortfoliosAPI.getPortfolio(!!portfolioId ? portfolioId : store.portfolio._id)
            .then((response) => {
                store.portfolio = response.data;
                store.selectedPortfolio = response.data;
            })
            .catch(error => {
                console.log(error);
            });
    },


    // get portfolios
    getUserPortfolios: async function () {
        const response = await PortfoliosAPI.getPortfolios(store.user._id);
        store.userPortfolios = sortDefaultPortfolioFirst(response.data);
        return store.userPortfolios;
    },

    loadPortfolioForSwitch: async function (portfolioShortId) {
        const portfolio = (await PortfolioActions.getUserPortfolios())
            .find(item => item.shortId === portfolioShortId);

        if (!portfolio?._id) {
            throw new Error("The selected portfolio could not be found");
        }

        const [portfolioPagesResponse, caseStudyPagesResponse] = await Promise.all([
            ProjectsAPI.getPortfolioPages(portfolio._id),
            ProjectsAPI.getCaseStudyPages(portfolio._id),
        ]);

        store.portfolio = portfolio;
        store.selectedPortfolio = portfolio;
        store.portfolioPages = portfolioPagesResponse.data.sort((a, b) => a.order - b.order);
        store.caseStudyPages = caseStudyPagesResponse.data.sort((a, b) => a.order - b.order);
        store.selectedProject = null;
        store.selectedSection = null;
        store.activeModal = null;
        store.activeModalData = null;
        store.showSidePanel = false;
        folioStore.commit("setPageToEdit", "home");
        store.portfolioSetupComplete = true;
        preloadSelectedPortfolioFonts();

        return portfolio;
    },

    getPortfolios: async function (portfolioId) {
        if (!store.user?._id) {
            await UserActions.ensureUser();
        }

        if (!store.user?._id) {
            throw new Error("Cannot load portfolios before the current user is available");
        }

        const currentPortfolioId = getCurrentRoutePortfolioId(portfolioId);
        await PortfoliosAPI.getPortfolios(store.user._id)
            .then(async (response) => {
                if(!response.data.length) {
                    console.log("No portfolios found");
                    store.showOnBoardingScreen = true;
                    await Router.push("/home/onboarding")
                }
                else {
                    store.userPortfolios = sortDefaultPortfolioFirst(response.data);
                    store.portfolio = store.userPortfolios.find(e => e.shortId === currentPortfolioId || e._id === currentPortfolioId) || store.userPortfolios[0];
                    store.selectedPortfolio = store.portfolio;

                    await PortfolioActions.getPortfolioPages(store.portfolio._id);
                    await PortfolioActions.getCaseStudyPages(store.portfolio._id);

                    let fontStyles = Object.entries(store.portfolio.designSettings.fontStyles).map((i) => i[1]);
                    GoogleFontsHelper.downloadSingleFonts(Object.entries(fontStyles).map((i) => i[1]));
                    //download unique fonts
                    await specificFontsLoader(fontStyles)
                    store.portfolioSetupComplete = true;
                }
            })
            .catch(error => {
                console.log("Error while getting portfolios", error);
                Router.push('/home')
                UserActions.refreshData();
            })
            .finally(() => {
                // if not a utility page and the portfolioID is missing than go home
                redirectToSelectedPortfolioIfNeeded();
            });
    },

    getPortfolioForEditor: async function (portfolioId) {
        const currentPortfolioId = getCurrentRoutePortfolioId(portfolioId);

        try {
            if (!currentPortfolioId) {
                store.showOnBoardingScreen = true;
                await Router.push("/home/onboarding");
                return;
            }

            const response = shouldFetchPortfolioByShortId(currentPortfolioId)
                ? await PortfoliosAPI.getPortfolioByShortId(currentPortfolioId)
                : await PortfoliosAPI.getPortfolio(currentPortfolioId);

            store.portfolio = response.data;
            store.selectedPortfolio = store.portfolio;
            store.userPortfolios = [store.portfolio];

            if (!store.portfolio) {
                await Router.push('/home');
                return;
            }

            await Promise.all([
                PortfolioActions.getPortfolioPages(store.portfolio._id),
                PortfolioActions.getCaseStudyPages(store.portfolio._id),
            ]);

            selectCurrentRouteProject();
            store.portfolioSetupComplete = true;
            preloadSelectedPortfolioFonts();
        } catch (error) {
            console.log("Error while getting editor portfolios", error);
            Router.push('/home');
            UserActions.refreshData();
        } finally {
            redirectToSelectedPortfolioIfNeeded();
        }
    },

    // get navigation items
    getPortfolioPages: async function(id, setHomePage) {
        if(id || store.portfolio) {
            try {
                let response = await ProjectsAPI.getPortfolioPages(!!id ? id : store.portfolio._id)

                store.portfolioPages = response.data.sort((a,b) => { return a.order - b.order } )
            }
            catch (e) {
                console.log('Error while getting portfolio pages.', e)
                //TODO: error
            } finally {
                if(setHomePage) {
                    folioStore.commit("setPageToEdit", "home");
                    emitter.emit('update-section-level-custom-colors');
                }
            }
        }
    },

    // get case study projects
    getCaseStudyPages: async function(portfolioId) {
        if(portfolioId || store.portfolio) {
            try {
                let response = await ProjectsAPI.getCaseStudyPages(!!portfolioId ? portfolioId : store.portfolio._id)
                store.caseStudyPages = response.data.sort((a,b) => { return a.order - b.order } )
                if(!store.project) store.project = store.caseStudyPages.filter(p => p.type === "home");
            }
            catch (e) {
                console.log('Error while getting case studies')
            }
        }
    },

    // get all case study projects
    getAllCaseStudyPages: async function(userId) {
        if( userId || store.user) {
            try {
                let response = await ProjectsAPI.getAllProject(!!userId ? userId : store.user._id, true)
                store.allCaseStudyPages = response.data.filter(p => !p.deletedAt && p.type === "caseStudy");
            }
            catch (e) {
                console.log('Error while getting All case studies')
            }
        }
    },

    // create new page
    createPage(projectType, name, link) {
        store.savingProject = true;

        if (projectType === 'file') {
            if(store.user.checklistStatus && !store.user.checklistStatus.steps.resume) {
                folioStore.commit('updateChecklistItem', ChecklistInteractionEnum.RESUME);
            }
        }

        let designSettings = store.portfolio.designSettings
        ProjectsAPI.createProject(
            store.user._id,
            store.portfolio._id,
            projectType,
            store.portfolioPages.filter(e => !e.deletedAt).length,
            name,
            link,
            null, // suggestions
            false, //is short case study
            designSettings,
            store.portfolio.templateSettings.defaultCaseStudyValues,
            store.portfolio.templateSettings.defaultThumbnailData

        )
            .then(response => {
                if(projectType === 'about' || projectType === 'contact' || projectType === 'blank') {
                    store.project = response.data;
                }
                folioStore.commit("addNewPageToList", response.data);
            })
            .catch(error => {
                PortfolioActions.handleCommonApiError(error);
            })
            .finally(() => {
                if(projectType === 'blank') PortfolioActions.trackEditEvent('Create Blank page');
                // PortfolioActions.getPortfolioPages();
                // PortfolioActions.getCaseStudyPages();
                // PortfolioActions.getAllCaseStudyPages();
                store.savingProject = false;
                if(projectType === 'about' || projectType === 'contact' || projectType === 'blank') {
                    Router.push(`/home/portfolios/${store.portfolio.shortId}/p/${store.project.shortId}/pages`)
                }
            });
    },

    // get page by short id
    async getPageByShortId(shortId) {
        ProjectsAPI.getProjectByShortId(store.portfolio.shortId, shortId)
            .then( response => {
                store.project = response.data;
                folioStore.commit("setAllowedSections", store.project.type);
            })
            .catch( error => {
                console.log(error)
                // page not found, go to home page
                folioStore.commit("setPageToEdit", "home");
                Router.push(`/home/portfolios/${store.portfolio.shortId}`)
            })
            .finally(() => {
                if(store.project) {
                    folioStore.commit("updateExistingStatSectionStyles");
                    folioStore.commit("updateExistingHeaderSectionStyle");
                    folioStore.commit("updateExistingHeaderSectionStyle");
                }
            });
    },

    updateProjectByShortId(shortId, titles, settings) {
        ProjectsAPI.updateProjectByShortId(store.portfolio.shortId, shortId, titles, settings)
            .then(response => {
                store.portfolioPages[store.portfolioPages.findIndex(e => e.shortId === shortId)] = response.data
            })
            .catch( error => {
                console.log(error)
            })
    },

    // delete page
    deletePage(pageId) {
        store.portfolioPages.find(e => e._id === pageId).deletedAt = 1;
        PortfolioActions.archivePage(pageId);
        PortfolioActions.reorderPages(true);
    },

    // reorder pages
    reorderPages(save) {
        const filteredMenuItems = store.portfolioPages.filter(e => !e.deletedAt)
        store.portfolioPages.forEach((e, index) => {
            if(!e.deletedAt) e.order = index
            if(!!e.deletedAt) e.order = filteredMenuItems.length + index
        })
        store.portfolioPages.sort((a, b) => { return a.order - b.order });

        if(save) PortfolioActions.updateNavItemsOrder();
    },

    // archive page
    archivePage(pageId) {
        ProjectsAPI.archiveProject(pageId)
            .then( response => {
                store.portfolioPages.find(e => e._id === pageId).deletedAt = !!response.data.deletedAt ? response.data.deletedAt : null;
                store.portfolioPages.find(e => e._id === pageId).draft = true;
            })
            .catch(error => {
                console.log(error)
            })
            .finally(() => {
                store.selectedProject = null;
                if( Router.currentRoute.value.fullPath.indexOf('/settings/archive') > -1 ) return
                if( !!store.portfolioPages.find(e => e._id === pageId && !e.deletedAt && (e.type === 'about' || e.type === 'contact' || e.type === 'blank') ) ) {
                    store.project = store.portfolioPages.find(e => e._id === pageId)
                    Router.push(`/home/portfolios/${store.portfolio.shortId}/p/${store.project.shortId}/pages`)
                } else if ( !!store.portfolioPages.find(e => e._id === pageId && !e.deletedAt && e.type === 'file') ) {
                    Router.back();
                }
            });
    },

    updatePageStyle() {
        store.savingProject = true;
        PortfoliosAPI.editPortfolio(store.portfolio)
            .then(response => {
                store.portfolio = response.data;
                store.selectedPortfolio = response.data;
                //PortfolioActions.updatePortfolioInUserObj();
            })
            .catch(error => { console.log(error) })
            .finally(() => {store.savingProject = false});
    },

    async updateNavButtonStyle(buttonStyle) {
        return await ProjectsAPI.updateNavButtonStyle(
            store.user._id,
            store.portfolio._id,
            buttonStyle
    )},

    updatePortfolio(field, skipStoreUpdate, keepModalOpen) {
        if(field) PortfolioActions.trackEditEvent(field);
        store.savingProject = true;
        PortfoliosAPI.editPortfolio(store.portfolio)
            .then(response => {
                if(!skipStoreUpdate) {
                    store.portfolio = response.data;
                    store.selectedPortfolio = response.data;
                    folioStore.commit("updatePortfolioInUserPortfolios", response.data);
                }
            })
            .catch(error => {
                console.log(error)
                store.activeModal = null;
                const errorObject = error.response.data;
                if(error.response.status === 405 && (errorObject.code === 1007)) {
                    folioStore.commit("setCurrentPlan", errorObject.currentSubscriptionPlan);
                    folioStore.commit("showAlertMessage", {
                        message: 'Upgrade to Full access to use this feature.',
                        type: 'alert',
                        interval: 10,
                    });
                }
            })
            .finally(() => {
                if(!keepModalOpen) {
                    store.activeModal = null;
                }
                store.savingProject = false;
                folioStore.commit("updateSidePanel", {allowSave: false});
            });
    },

    updatePortfolioLogo(logo) {
        store.savingProject = true;
        return PortfoliosAPI.updatePortfolioLogo(logo, store.portfolio._id)
            .then(response => {
                if (response?.data) {
                    folioStore.commit("updatePortfolioLogo", response.data);
                }
            })
            .catch(error => {
                console.log(error);
                store.activeModal = null;
                const errorObject = error.response?.data;
                if (error.response?.status === 405 && errorObject?.code === 1007) {
                    folioStore.commit('setCurrentPlan', errorObject.currentSubscriptionPlan);
                    folioStore.commit('showAlertMessage', {
                        message: 'Upgrade to Full access to use this feature.',
                        type: 'alert',
                        interval: 10,
                    });
                }
            })
            .finally(() => {
                store.savingProject = false;
            });
    },

    updatePortfolioNavigation(navigation) {
        store.savingProject = true;
        return PortfoliosAPI.updatePortfolioNavigation(navigation, store.portfolio._id)
            .then(response => {
                if (response?.data) {
                    folioStore.commit("updatePortfolioNavigation", response.data);
                }
            })
            .catch(error => {
                console.log(error);
                store.activeModal = null;
            })
            .finally(() => {
                store.savingProject = false;
            });
    },

    updatePortfolioHero(hero) {
        store.savingProject = true;
        return PortfoliosAPI.updatePortfolioHero(hero, store.portfolio._id)
            .then(response => {
                if (response?.data) {
                    folioStore.commit("updatePortfolioHeroSection", response.data);
                }
            })
            .catch(error => {
                console.log(error);
                store.activeModal = null;
            })
            .finally(() => {
                store.savingProject = false;
            });
    },

    updatePortfolioDesignSettings(designSettings) {
        store.savingProject = true;
        return PortfoliosAPI.updatePortfolioDesignSettings(designSettings, store.portfolio._id)
            .then(response => {
                if (response?.data) {
                    folioStore.commit("updatePortfolioDesignSettings", response.data);
                }
            })
            .catch(error => {
                console.log(error);
                store.activeModal = null;
            })
            .finally(() => {
                store.savingProject = false;
            });
    },

    updatePortfolioCursorType(cursorType) {
        store.savingProject = true;
        PortfoliosAPI.updatePortfolioCursorType(cursorType, store.portfolio._id)
            .then(response => {
                if (response?.data) {
                    store.portfolio.templateSettings.cursorType = response.data;
                }
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                store.savingProject = false;
                folioStore.commit("updateSidePanel", {allowSave: false});
            });
    },

    setPortfolioGlobalSpacing() {
        store.savingProject = true;
        PortfoliosAPI.setPortfolioGlobalSpacing(store.project)
            .then(response => {
                store.project = response.data.find(e => e._id === store.project._id);
                store.portfolioPages = response.data;
            })
            .catch(error => { console.log(error) })
            .finally(() => {store.savingProject = false});
    },

    updateNavItemsOrder() {
        let pages = []
        store.portfolioPages.forEach((e, index) => pages.push({'projectId': e._id, 'order': e.order}))
        store.savingProject = true;
        ProjectsAPI.updatePortfolioPagesOrder(store.portfolio._id, pages)
            .then(response => {})
            .catch(error => console.log(error))
            .finally(() => {
                // update portfolio to keep navItems in sync
                PortfolioActions.updatePortfolio()
                store.savingProject = false
                folioStore.commit("updateSidePanel", {allowSave: false});
            })
    },

    updateCaseStudiesOrder() {
        store.caseStudyPages.sort((a, b) => a.order - b.order);
        store.savingProject = true;
        ProjectsAPI.updateCaseStudyPagesOrder(store.portfolio._id, store.caseStudyPages)
            .then(response => {
                PortfolioActions.getCaseStudyPages();
            })
            .catch(error => console.log(error))
            .finally(() => {
                store.savingProject = false
            })
    },

    setDefaultPagesForPortfolio(portfolio) {
        store.savingProject = true;
        PortfoliosAPI.addDefaultPortfolioPages(portfolio)
            .then(response => {
                store.portfolioPages = response.data.sort((a,b) => { return a.order - b.order } );
            })
            .catch(error => {console.log(error)})
    },

    getPortfolioTemplateSettings() {
        store.savingProject = true;
        PortfoliosAPI.getPortfolioTemplateSettings(store.portfolio._id)
            .then(response => {
                store.portfolio.templateSettings = response.data;
            })
            .catch(error => {console.log(error)})
            .finally(() => {
                store.savingProject = false;
            })
    },

    getPortfolioTemplatePreview() {
        PortfoliosAPI.getPortfolioTemplatePreview(store.portfolio.shortId)
            .then(response => {
                store.portfolio.templatePreview = response.data;
            })
            .catch(error => {
                console.log(`There was an error fetching current portfolio preview: ${error}`)
            })
    },

    getTemplatePreviews() {
        PortfoliosAPI.getTemplatePreviews()
            .then(response => {
                store.templatePreviews = response.data;
            })
            .catch(error => {
                console.log(`There was an error fetching template previews: ${error}`)
            })
    },

    async prefetchTemplatePreviews() {
        if (store.templatePreviews?.length) {
            return store.templatePreviews;
        }

        if (!templatePreviewsPromise) {
            templatePreviewsPromise = PortfoliosAPI.getTemplatePreviews()
                .then(response => {
                    store.templatePreviews = response.data;
                    return response.data;
                })
                .catch(error => {
                    console.log(`There was an error prefetching template previews: ${error}`);
                    return [];
                })
                .finally(() => {
                    templatePreviewsPromise = null;
                });
        }

        return templatePreviewsPromise;
    },

    getPortfolioTemplateAndDesignSettings() {
        store.savingProject = true;
        PortfoliosAPI.getPortfolio(store.portfolio._id)
            .then(response => {
                console.log(response.data);
                folioStore.commit("updatePortfolioTemplateSettings", response.data.templateSettings);
                folioStore.commit("updatePortfolioDesignSettings", response.data.designSettings);
                // TODO use a mutation here. Current updatePortfolioHeroSection fucks up the modules list since it using deepMerge and modules are duplicated.
                // updatePortfolioNavigation and updatePortfolioLogo are good, but had no time to properly test them.
                store.portfolio.portfolioHero = response.data.portfolioHero
                store.portfolio.portfolioNavigation = response.data.portfolioNavigation
                store.portfolio.logo = response.data.logo
            })
            .catch(error => {console.log(error)})
            .finally(() => {
                store.savingProject = false;
            })
    },

    getPortfolioSettings() {
        store.savingProject = true;
        PortfoliosAPI.getPortfolioSettings(store.portfolio._id)
            .then(response => {
                store.portfolio.settings = response.data;
            })
            .catch(error => {console.log(error)})
            .finally(() => {
                store.savingProject = false;
            })
    },

    switchPortfolioDraft(portfolio) {
        PortfolioActions.trackEditEvent(`${portfolio.draft ? 'Publish' : 'Unpublish'} portfolio`);
        return PortfoliosAPI.setPortfolioDraft(portfolio._id, !portfolio.draft)
            .then(response => {
                if(response.data === true) {
                    PortfolioActions.getPortfolioPages();
                }
                folioStore.commit("showAlertMessage", {
                    type:'success',
                    message: portfolio.draft ? `Your portfolio is live on:  <span class="bold">${folioStore.getters.pageViewURL.replace(/^https?:\/\//, '')}</span>`: 'Your portfolio is set to draft',
                    interval:5,
                })

                store.portfolio.draft = !store.portfolio.draft;
                return true;
            }).catch(error => {
            store.activeModal = null;
            if(error.response && error.response.data) {
                let errorObject = error.response.data;
                if(error.response.status === 405 && (errorObject.code === 1001 || errorObject.code === 1002)) {
                    store.currentSubscriptionPlan = errorObject.currentSubscriptionPlan;
                    folioStore.commit("showAlertMessage", {
                        message: 'Publishing failed. Please try again!',
                        interval: 5,
                    });
                }
            }
            return false;
        })
            .finally(() => store.selectedPortfolio = null)
    },

    switchProjectDraft(project) {
        PortfolioActions.trackEditEvent(`${project.draft ? 'Publish' : 'Unpublish'} project`);
        ProjectsAPI.setProjectDraft(project._id, !project.draft)
            .then(response => {
                if(response.data === true) {
                    PortfolioActions.getCaseStudyPages();
                }

                folioStore.commit("showAlertMessage", {
                    message: project.draft
                        ? store.portfolio?.draft
                            ? 'Your case study will be visible on your live site once you publish your portfolio.'
                            : 'Your case study is visible on your live site.'
                        : 'Your case study is hidden from your live site. Only you can see it in the editor.',
                    interval: 5,
                    type: 'success'
                });

                if(getters.isCaseStudy) {
                    store.project.draft = !store.project.draft;
                    console.log('store', store.project)
                }
            }).catch(error => {
                store.activeModal = null;
                if(error.response && error.response.data) {
                    let errorObject = error.response.data;
                    if(error.response.status === 405 && (errorObject.code === 1001 || errorObject.code === 1002)) {
                        store.currentSubscriptionPlan = errorObject.currentSubscriptionPlan;
                        folioStore.commit("showAlertMessage", {
                            message: 'Action failed. Please try again!',
                            type: 'alert',
                            interval: 5,
                        });
                    }
                }
            })
            .finally(() => store.selectedProject = null)
    },

    deletePortfolioImage(imageUrl, modal) {
        store.savingProject = true;
        PortfoliosAPI.deletePortfolioImage(store.portfolio._id, imageUrl)
            .then(response =>{})
            .catch(response => {})
            .finally(() => {
                store.savingProject = false;
                if(!!modal) store.activeModal = null;
            });
    },

    updatePortfolioLegacyLogo(gaLabel) {
        store.savingProject = true;
        PortfoliosAPI.updatePortfolioLegacyLogo(store.portfolio.templateSettings.logo, store.portfolio._id)
            .then(response => {
                if(gaLabel) PortfolioActions.trackEditEvent(gaLabel);
                store.portfolio.templateSettings.logo = response.data.templateSettings.logo;
            })
            .catch(error => {
                if(error.response.status === 405) {
                    store.currentSubscriptionPlan = error.response.data.currentSubscriptionPlan;
                    folioStore.commit("showAlertMessage", {
                        message: 'Upgrade to Full access to use this feature.',
                        type: 'alert',
                        interval: 10,
                    });
                }
            })
            .finally(() => {
                store.savingProject = false;
                folioStore.commit("navigateBetweenSidePanels", {mainPanel: SettingsPanelPathsEnum.ROOT});
            })
    },

    // notifications actions
    getLatestNotifications: async function () {
        if(store.user) {
            await NotificationsAPI.getAllNotificationForUser(
                store.user._id
            )
                .then(async response => {
                    const notifications = Array.isArray(response.data) ? response.data : [];
                    if (notifications.some(n => n.type === 'System')) {
                        await UserActions.getUserSeenNotifications();
                    }
                    store.notifications = notifications;
                })
                .catch(error => {
                    console.log(error);
                });
        }
    },
    clickNotification(notification, alreadySeen) {
        PortfolioActions.trackEditEvent('Click notification in dropdown');
        if(!alreadySeen)  {
            NotificationsAPI.setNotificationSeenBy(
                store.user._id,
                notification.id
            ).then(async () => {
                if(notification.type === 'System') {
                    addSeenSystemNotifications([notification.id]);
                    await UserActions.getUserSeenNotifications();
                } else {
                    await PortfolioActions.getLatestNotifications();
                }
            }).catch(error => {
                console.log(error);
            });
        }
        PortfolioActions.openReview(notification);
    },
    setAllNotificationSeen() {
        PortfolioActions.trackEditEvent('Click notification in dropdown');
        NotificationsAPI.setNotificationSeenBy(
            store.user._id,
            null
        ).then(async response => {
            const notifications = Array.isArray(response.data) ? response.data : [];
            const systemNotificationIds = notifications
                .filter(n => n.type === 'System')
                .map(n => n.id);

            if(systemNotificationIds.length) {
                addSeenSystemNotifications(systemNotificationIds);
                store.notifications = notifications;
                await UserActions.getUserSeenNotifications();
            } else {
                store.notifications = notifications;
            }
        }).catch(error => {
            console.log(error);
        });
    },
    async openReview(notification) {
        if(notification.navigation) {
            if(notification.navigation === 'Upgrade') {
                MixpanelService.track("UPGRADE_CTA_CLICKED",
                    {
                        ...MixpanelService.getUrls(),
                        "event_group": MixpanelService.MixpanelEventGroups.USER_LIFECYCLE,
                        "source": "Notification",
                        "context": "Notification",
                        "to": "Payment"
                    }
                )

                if(store.currentSubscriptionPlan === "Trial") {
                    await Router.push({path:'/home/payment', state: { subscriptionData: { billingCycle:'monthly' }}});
                } else {
                    await Router.push({ path: `/home/prices` });
                }
            }
            else if(notification.navigation === "Account") {
                window.location.href = "/home/settings/account-settings";
            }
            else if(notification.navigation === 'Review') {
                await PortfolioActions.handleReviewRedirect(notification);
            }
            else if(notification.navigation === 'Animations') {
                await Router.push({ path: `/home/portfolios/${store.portfolio.shortId}/style/animation-settings` });
            }
            else if(notification.navigation === 'Pricing') {
                await Router.push({ path: `/home/prices` });
            }
            else if(notification.navigation === 'NotificationsPage') {
                await Router.push({ path: `/home/notifications` });
            }
            else {
                window.location.href = "/home/notifications";
            }
        }
    },
    async handleReviewRedirect(notification) {
        if(notification.additionalInfo.type === 'ReviewSubmitted') {
            let url = window.location.origin + "/myreviews/" + notification.additionalInfo.shortId + "?reviewer=" + notification.additionalInfo.fromUserId;
            window.open(url, '_blank').focus();
        }
        else if(notification.additionalInfo.type === 'FollowupSentToOwner') {
            let url = window.location.origin + "/myreviews/" + notification.additionalInfo.shortId + "?reviewer=" + notification.additionalInfo.fromUserId + "&followup=true";
            window.open(url, '_blank').focus();
        }
        else if(notification.additionalInfo.type === 'FollowupSentToReviewer') {
            let url = window.location.origin + "/review/" + notification.additionalInfo.shortId + "?followup=true";
            window.open(url, '_blank').focus();
        }
    },

    handleCommonApiError(error){
        if(!!error.response && error.response.status === 405) {
            store.currentSubscriptionPlan = error.response.data.currentSubscriptionPlan;
            folioStore.commit("showAlertMessage", {
                message: 'Upgrade to Full access to use this feature.',
                type: 'alert',
                interval: 10,
            });
        }
    },

    // tracking actions
    trackPageView(url, label) {
        pageview({
            'page_path':url,
            'page_title':label,
            'page_location':location.href
        });
    },
    trackPaywallEvent(location) {
        let event_action = "Paywall appeared";
        try {
            event(event_action, {
                'location': location,
                'event_label': `User: ${store.user._id}, Portfolio: ${store.portfolio ? store.portfolio._id : 'none'}`
            });
        }
        catch(err) {
            console.log('Error while sending GEVENT: ' + err)
        }
    },
    trackEditEvent(eventAction, eventLabelParam) {
        if (!shouldTrackEditorEvents()) return;

        // [eventCategory], [eventAction], [eventLabel]
        // [eventLabel]: User: [userId], Portfolio: [portfolioId], eventLabelParam
        const editorName =  !!store.sectionEditorSettings.pageToEdit ?
                            `${store.sectionEditorSettings.pageToEdit.charAt(0).toUpperCase() + store.sectionEditorSettings.pageToEdit.slice(1)} page` :
                            'Profile';

        event(eventAction, {
            'event_category': (store.project && store.project.type) !== PageTypesEnum.CASE_STUDY ? `${editorName} editor` : 'Project editor',
            'event_label': eventLabelParam
                ? `User: ${store.user._id}, Portfolio: ${store.portfolio ? store.portfolio._id : 'none'}, ${eventLabelParam}`
                    : `User: ${store.user._id}, Portfolio: ${store.portfolio ? store.portfolio._id : 'none'}`
        });

        event('editor_action', {
            'event_source': (store.project && store.project.type) !== PageTypesEnum.CASE_STUDY ? `${editorName} editor` : 'Project editor',
            'event_name': eventAction,
            'event_params': eventLabelParam
                ? `User: ${store.user._id}, Portfolio: ${store.portfolio ? store.portfolio._id : 'none'}, ${eventLabelParam}`
                : `User: ${store.user._id}, Portfolio: ${store.portfolio ? store.portfolio._id : 'none'}`
        });
    },

    // helper actions
    async getAllAvalibleFonts() {
        await SettingsFontAPI.getFonts("").then(response => {
            store.fonts = response.data;
        }).catch(error => {
            console.log(error);
        });
    },

    async getTemplates() {
        try {
            let response = await ApiHelper.get("/api/portfolio-template");
            store.portfolioTemplates = response.data;

            const { data } = await ApiHelper.get("/api/portfolio-template/styles");
            const styles = data.sort((a, b) => a.order - b.order);
            folioStore.commit("setPortfolioTemplateStyles", styles)
        }
        catch (e) {
            console.log('failed to get template details: ', e)
        }
    }
};

export function TrackPageView(url, label) {
    //TODO: check google analytics
    pageview({
        'page_path':url,
        'page_title':label,
        'page_location':location.href
    });
}
